/**
 * Centralised role helpers.
 *
 * A user can have several roles (#285). The login response and the users API send
 * `roles` (every role code, primary first) and `role` (the primary role). Permissions are
 * the UNION of all roles: menus and guards use hasRole / hasAnyRole / canAccess.
 * The primary role (getUserRole) is only for the home screen and defaults.
 *
 * Some legacy code checks `role_code`, and a session stored before #285 has no `roles`;
 * these helpers normalise every shape so callers never need to worry about it.
 */

const roleCode = (r) => ((r && typeof r === 'object' ? r.role_code : r) || '').toUpperCase();

/** Primary role code (home screen and defaults only, not permissions). */
export const getUserRole = (user) =>
  (user?.role_code || user?.role || '').toUpperCase();

/** Every role code of the user, primary first. */
export const getUserRoles = (user) => {
  if (Array.isArray(user?.roles)) return user.roles.map(roleCode).filter(Boolean);
  const primary = getUserRole(user);
  return primary ? [primary] : [];
};

/** True if any of the user's roles is in `roles` (array or arguments). */
export const hasAnyRole = (user, ...roles) => {
  const wanted = roles.flat().map(r => r.toUpperCase());
  return getUserRoles(user).some(r => wanted.includes(r)) || !!user?.is_superuser;
};

export const hasRole = hasAnyRole;

export const isAdmin = (user) =>
  getUserRoles(user).includes('ADMIN') || !!user?.is_superuser;

// Maps page IDs to the roles that may access them.
// ADMIN always has access (enforced in canAccess, not listed per page).
export const PAGE_ACCESS = {
  home:        ['MANAGER', 'SUPERVISOR', 'OPERATOR', 'PLANNER', 'WAREHOUSE', 'COSTING', 'SALES', 'USER', 'READONLY', 'QUALITY'],
  production:  ['MANAGER', 'SUPERVISOR', 'PLANNER', 'COSTING'],
  products:    ['MANAGER', 'PLANNER', 'COSTING', 'SALES'],
  sales:       ['MANAGER', 'COSTING', 'SALES'],
  procurement: ['MANAGER', 'PLANNER', 'WAREHOUSE', 'COSTING'],
  inventory:   ['MANAGER', 'SUPERVISOR', 'PLANNER', 'WAREHOUSE', 'COSTING'],
  qc:          ['MANAGER', 'SUPERVISOR', 'QUALITY'],   // read for MANAGER; writes: SUPERVISOR, QUALITY (#285)
  admin:       [],   // ADMIN only (handled by isAdmin check in canAccess)
  ai:          ['MANAGER'],   // ADMIN + MANAGER, same as backend/ai (#110, #380)
};

/**
 * Returns true if the user may navigate to `pageId` (any of the user's roles counts).
 * ADMIN / is_superuser always wins.
 * Unknown page IDs are allowed by default (fail-open for sub-pages).
 */
export const canAccess = (user, pageId) => {
  if (!user || !pageId) return false;
  if (isAdmin(user)) return true;
  const allowed = PAGE_ACCESS[pageId.toLowerCase()];
  if (allowed === undefined) return true;          // unknown page — allow
  return getUserRoles(user).some(r => allowed.includes(r));
};
